/** Crawl Check's simulated probes and scoring membership, safe for client imports.
 * Vendor roles: https://developers.openai.com/api/docs/bots and
 * https://support.claude.com/en/articles/8896518
 */
import type { CrawlCheckResult } from './crawl-check'

export type CrawlBotResult = CrawlCheckResult['botAccess'][number] & {
  verifiable?: boolean
}

export interface CrawlBotDef {
  name: string
  category: 'search' | 'ai-browse' | 'ai-train' | 'seo'
  userAgent: string
  scoringRole?: 'retrieval' | 'search'
  /**
   * A robots.txt token with no HTTP user agent of its own. Its rules are read,
   * but no test request is sent, since no real request ever carries it.
   */
  tokenOnly?: true
  /** One line shown under the bot's name in results. */
  note?: string
}

export const CRAWL_BOTS: readonly CrawlBotDef[] = [
  {
    name: 'Googlebot',
    category: 'search',
    scoringRole: 'search',
    userAgent:
      'Mozilla/5.0 (compatible; Googlebot/2.1; +http://www.google.com/bot.html)',
  },
  {
    name: 'Bingbot',
    category: 'search',
    scoringRole: 'search',
    userAgent:
      'Mozilla/5.0 (compatible; bingbot/2.0; +http://www.bing.com/bingbot.htm)',
  },
  {
    name: 'Applebot',
    category: 'search',
    scoringRole: 'search',
    userAgent: 'Mozilla/5.0 (Applebot/0.1; +http://www.apple.com/go/applebot)',
  },
  {
    name: 'GPTBot',
    category: 'ai-train',
    userAgent:
      'Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; GPTBot/1.4; +https://openai.com/gptbot',
  },
  {
    name: 'ClaudeBot',
    category: 'ai-train',
    userAgent: 'ClaudeBot',
  },
  {
    name: 'OAI-SearchBot',
    category: 'ai-browse',
    scoringRole: 'retrieval',
    userAgent:
      'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/131.0.0.0 Safari/537.36; compatible; OAI-SearchBot/1.4; +https://openai.com/searchbot',
  },
  {
    name: 'ChatGPT-User',
    category: 'ai-browse',
    scoringRole: 'retrieval',
    note: 'User-triggered · OpenAI says robots.txt may not apply',
    userAgent:
      'Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; ChatGPT-User/1.0; +https://openai.com/bot',
  },
  {
    name: 'Claude-SearchBot',
    category: 'ai-browse',
    scoringRole: 'retrieval',
    userAgent: 'Claude-SearchBot',
  },
  {
    name: 'Claude-User',
    category: 'ai-browse',
    scoringRole: 'retrieval',
    userAgent: 'Claude-User',
  },
  {
    name: 'PerplexityBot',
    category: 'ai-browse',
    scoringRole: 'retrieval',
    userAgent:
      'Mozilla/5.0 (compatible; PerplexityBot/1.0; +https://perplexity.ai/perplexitybot)',
  },
  {
    // General-purpose diagnostic; keep the serialized category for compatibility.
    name: 'GoogleOther',
    category: 'ai-browse',
    note: 'General-purpose crawler · diagnostic only',
    userAgent: 'Mozilla/5.0 (compatible; GoogleOther)',
  },
  {
    name: 'CCBot',
    category: 'ai-train',
    userAgent: 'CCBot/2.0 (https://commoncrawl.org/faq/)',
  },
  {
    // Google: "Google-Extended doesn't have a separate HTTP request user agent
    // string." It controls Gemini training and grounding, not Search.
    name: 'Google-Extended',
    category: 'ai-train',
    userAgent: 'Google-Extended',
    tokenOnly: true,
    note: 'robots.txt token only · Gemini training and grounding, not Search',
  },
  {
    name: 'Bytespider',
    category: 'ai-train',
    userAgent: 'Mozilla/5.0 (compatible; Bytespider)',
  },
  {
    name: 'Meta-ExternalAgent',
    category: 'ai-train',
    userAgent: 'Mozilla/5.0 (compatible; Meta-ExternalAgent/1.0)',
  },
  { name: 'cohere-ai', category: 'ai-train', userAgent: 'cohere-ai' },
  {
    name: 'AhrefsBot',
    category: 'seo',
    userAgent:
      'Mozilla/5.0 (compatible; AhrefsBot/7.0; +http://ahrefs.com/robot/)',
  },
]

export const CRAWL_RETRIEVAL_BOT_NAMES = [
  ...new Set(
    CRAWL_BOTS.filter(bot => bot.scoringRole === 'retrieval').map(
      bot => bot.name
    )
  ),
]
export const CRAWL_SEARCH_BOT_NAMES = [
  ...new Set(
    CRAWL_BOTS.filter(bot => bot.scoringRole === 'search').map(bot => bot.name)
  ),
]

export function isCrawlRetrievalBot(botName: string): boolean {
  return CRAWL_RETRIEVAL_BOT_NAMES.includes(botName)
}
